import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Menu, X, PanelLeftClose, PanelLeftOpen, BookOpen } from "lucide-react";
import { elakayAutoText } from "../dataElakay";
import "./howWeDoItSidebar.css";

// Persisted so the collapse choice survives navigation between the guides
// list and a tutorial's detail page (both mount this same component).
const COLLAPSE_STORAGE_KEY = "hwdi_sidebar_collapsed";

const readStoredCollapsed = () => {
  try {
    return window.localStorage.getItem(COLLAPSE_STORAGE_KEY) === "1";
  } catch (e) {
    return false;
  }
};

// Desktop: plain sidebar, collapsible to a slim icon rail. Mobile: a
// self-contained slide-in drawer (the site's shared SideDrawer is
// hard-wired to the main user menu, so this module ships its own
// lightweight drawer rather than forcing a bad fit).
const HowWeDoItSidebar = ({ categories = [], tutorials = [], currentSlug }) => {
  const [query, setQuery] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readStoredCollapsed);

  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSE_STORAGE_KEY, collapsed ? "1" : "0");
    } catch (e) {
      // Storage unavailable (private mode, quota) -- collapse state simply
      // won't persist across page loads, nothing else depends on it.
    }
  }, [collapsed]);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q ? tutorials.filter((t) => t.title.toLowerCase().includes(q)) : tutorials;
    const byCategory = {};
    filtered.forEach((t) => {
      const catId = t.category?._id || t.category || "uncategorized";
      if (!byCategory[catId]) byCategory[catId] = [];
      byCategory[catId].push(t);
    });
    return categories
      .map((cat) => ({ category: cat, items: byCategory[cat._id] || [] }))
      .filter((group) => group.items.length > 0);
  }, [categories, tutorials, query]);

  const content = (
    <>
      <div className="hwdi-sidebar-header">
        <span><BookOpen size={16} /> {elakayAutoText("howWeDoIt_mainTitle", "How We Do It in Elakay")}</span>
        <button
          type="button"
          className="hwdi-sidebar-collapse-btn"
          onClick={() => setCollapsed((v) => !v)}
          title={elakayAutoText("howWeDoIt_sidebar_collapse", "Reduire le menu")}
          aria-label={elakayAutoText("howWeDoIt_sidebar_collapse", "Reduire le menu")}
        >
          <PanelLeftClose size={17} />
        </button>
        <button
          type="button"
          className="hwdi-sidebar-close"
          onClick={() => setMobileOpen(false)}
          aria-label={elakayAutoText("howWeDoIt_sidebar_closeMenu", "Fermer le menu")}
        >
          <X size={18} />
        </button>
      </div>
      <div className="hwdi-sidebar-search">
        <Search size={14} />
        <input
          type="search"
          placeholder={elakayAutoText("howWeDoIt_sidebar_search", "Rechercher...")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label={elakayAutoText("howWeDoIt_sidebar_search", "Rechercher...")}
        />
      </div>
      <nav className="hwdi-sidebar-nav">
        {grouped.map(({ category, items }) => (
          <div key={category._id} className="hwdi-sidebar-group">
            <div className="hwdi-sidebar-group-title">{category.icon} {category.name}</div>
            <ul>
              {items.map((item) => (
                <li key={item._id}>
                  <Link
                    to={`/how-we-do-it/${item.slug}`}
                    className={item.slug === currentSlug ? "active" : ""}
                    onClick={() => setMobileOpen(false)}
                  >
                    {item.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {grouped.length === 0 && <div className="hwdi-sidebar-empty">{elakayAutoText("howWeDoIt_sidebar_empty", "Aucun resultat.")}</div>}
      </nav>
    </>
  );

  return (
    <>
      <button type="button" className="hwdi-sidebar-mobile-trigger" onClick={() => setMobileOpen(true)}>
        <Menu size={16} /> {elakayAutoText("howWeDoIt_sidebar_menu", "Sommaire")}
      </button>

      <aside className={`hwdi-sidebar hwdi-sidebar--desktop ${collapsed ? "is-collapsed" : ""}`}>
        {collapsed ? (
          <button
            type="button"
            className="hwdi-sidebar-expand-btn"
            onClick={() => setCollapsed(false)}
            title={elakayAutoText("howWeDoIt_sidebar_expand", "Afficher le menu")}
            aria-label={elakayAutoText("howWeDoIt_sidebar_expand", "Afficher le menu")}
          >
            <PanelLeftOpen size={18} />
          </button>
        ) : (
          content
        )}
      </aside>

      <div className={`hwdi-sidebar-backdrop ${mobileOpen ? "open" : ""}`} onClick={() => setMobileOpen(false)} />
      <aside className={`hwdi-sidebar hwdi-sidebar--mobile ${mobileOpen ? "open" : ""}`}>{content}</aside>
    </>
  );
};

export default HowWeDoItSidebar;
